import { ChangeDetectorRef, Component, DestroyRef, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { interval, Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

import {
	NavigationMenuIcons,
} from '@itc-core/components/navigation-panels/navigation-menu/common/navigation-menu.constants';
import {
	NavigationMenuConfig,
} from '@itc-core/components/navigation-panels/navigation-menu/common/navigation-menu.models';
import { whiteLabelConfig } from '../config/config';
import { environment } from '../environments/environment';
import { CommunicationService } from './core/components/communication/common/communication.service';
import { Constants, NavigationMenuItems, TIMES_IN_MILLISECONDS } from './helpers';
import { AuthUserModel, UserModel } from './models';
import { AuthService } from './services/auth.service';
import { EstimatorUserService } from './services/estimator-user/estimator-user.service';
import { ExportService } from './services/export.service';
import { IntercomService } from './services/intercom.service';
import { LocalStorageService } from './services/local-storage.service';
import { ProjectTimeService } from './services/project-time.service';
import { RolePermissionsService } from './services/role-permissions.service';
import { UserTimeService } from './services/user-time-service';
import { CacheService } from './services/cache.service';
import { FeatureFlagService } from './services/feature-flag.service';
import { ProductTourService } from './services/product-tour.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Feature } from './helpers/feature';
import { CommandPaletteComponent } from '@mos-core/components/command-palette/command-palette.component';
import posthog from 'posthog-js';
import { TranslocoService } from '@jsverse/transloco';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';
import { NavRoute } from './interfaces/nav-route';
import { LibrariesService } from './pages/libraries/libraries/common/libraries.service';
import { SettingsService } from './services/settings.service';
import { DomStyleService } from './services/dom-style.service';
import { ScrollService } from './services/scroll-service';
import { AccountingAuditService } from './services/accounting-audit.service';
import { ExternalAccountingService } from './services/external-accounting.service';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
	@ViewChild(CommandPaletteComponent) commandPalette: CommandPaletteComponent;
	public canCreateNewProject: boolean = false;
	public communicationObservable: Observable<String[]>;
	public currentLanguage: string;
	public featuresMenuIsCollapsed = true;
	public isActive = false;
	public isAuthenticated = false;
	public isContractsAndAbove = false;
	public isEntityActive = false;
	public isEmployee: boolean = false;
	public navConfig: NavigationMenuConfig = new NavigationMenuConfig();
	public navigationMenuItems: typeof NavigationMenuItems = NavigationMenuItems;
	public sassAdminMessages = false;
	public selectedRoute: string;
	public settingsMenuIsCollapsed = true;
	public SVG_ICON_PATHS = Constants.SVG_ICON_PATHS;
	public userLoader: Observable<UserModel>;
	public userMenuIsCollapsed = true;
	public userPicture = Constants.PROFILE_DEFAULT_PICTURE;
	public isApproval: boolean = false;
	public activeTour?: string;
	public isResource = false;
	public isCreateInvoice = false;
	public isMeasure: boolean = false;
	public creatingProject = false;
	public isOnSettings = false;
	public isOnLibraries = false;
	public isOnGantt: boolean = false;
	public isOnSalesPipeline: boolean = false;
	public isOnExternalAccounting = false;
	public libraryRoutes: NavRoute[] = [];
	public settingsRoutes: NavRoute[] = [];
	public termsAndConditionsAccepted: boolean | null = true; // assume true until checked to prevent flickering
	public currentUser: UserModel;
	public hasIntegrationWarning: boolean = false;
	public accountingProvider: string;

	private destroyRef = inject(DestroyRef);
	private saasAdminMessagesSubscription: Subscription = new Subscription();

	constructor(
		private authService: AuthService,
		private estimatorUserService: EstimatorUserService,
		private localStorageService: LocalStorageService,
		private intercomService: IntercomService,
		private rolePermissions: RolePermissionsService,
		private userTimeService: UserTimeService,
		private projectTimeService: ProjectTimeService,
		private router: Router,
		private changeDetectorRef: ChangeDetectorRef,
		public activatedRoute: ActivatedRoute,
		private exportService: ExportService,
		private communicationService: CommunicationService,
		private cacheService: CacheService,
		private tourService: ProductTourService,
		private features: FeatureFlagService,
		private translocoService: TranslocoService,
		private translocoLocaleService: TranslocoLocaleService,
		private librariesService: LibrariesService,
		private settingsService: SettingsService,
		private domStyleService: DomStyleService,
		private scrollService: ScrollService,
		private accountingAuditService: AccountingAuditService,
		private externalAccountingService: ExternalAccountingService,
	) {
		// set the tab title from the config
		document.getElementsByTagName('title')[0].innerHTML = whiteLabelConfig.app.title;

		this.tourService.activeTourSubject.pipe(takeUntilDestroyed()).subscribe(v => (this.activeTour = v));
	}

	// Base page content condition
	private get _basePageContentCondition(): boolean {
		return (
			this.isAuthenticated &&
			!this.isApproval &&
			!this.isResource &&
			!this.creatingProject &&
			!this.isCreateInvoice
		)
	}

	// Standard main content - base main content case
	public get shouldUseMainContent(): boolean {
		return this._basePageContentCondition;
	}

	// Page content - sidebar visibility
	public get shouldShowSidebar(): boolean {
		return (
			this._basePageContentCondition &&
			this.isEntityActive &&
			!this.isEmployee &&
			!this.isMeasure
		)
	}

	// Page content - full page visibility
	public get shouldShowFullPage(): boolean {
		return (
			this._basePageContentCondition &&
			!this.isMeasure &&
			!this.isOnGantt &&
			!this.isOnSalesPipeline
		)
	}

	public ngOnInit() {
		this.domStyleService.applyThemeColours(environment.colors);
		this.handleAuthState();
		this.configurePostHog();
		this.currentLanguage = this.translocoService.getActiveLang();
		this.startSaasAdminMessagePolling();
		this.initUserDataStream();
		this.handleNavigationAndUpdateUIFlags();
		this.checkForAdminMessages();
	}

	public ngAfterContentChecked() {
		this.changeDetectorRef.detectChanges();
	}

	public onActivate() {
		const scrollToTop = window.setInterval(() => {
			const pos = window.pageYOffset;
			if (pos > 0) {
				window.scroll(0, -1); // how far to scroll on each step
			} else {
				window.clearInterval(scrollToTop);
			}
		}, 0.5);
	}

	// This handles the user closing/refreshing the page
	@HostListener('window:beforeunload', ['$event'])
	public beforeUnloadHander(event) {
		const projectTimeId = this.projectTimeService.getCurrentProjectTimeSession();
		this.userTimeService.complete(projectTimeId);
		this.localStorageService.clearCurrentSessionTimeStart();
	}

	// listen for the escape key to close the command palette
	@HostListener('document:keydown', ['$event'])
	public handleKeyboardEvent(event: KeyboardEvent) {
		if (event.key === 'Escape' && this.commandPalette.isOpen) {
			this.commandPalette.close();
		}
	}

	/**
	 * Controls dropdown menus that appear on mobile.
	 * @param menu
	 */
	public selectMenu(menu?: string): void {
		if (menu === 'user') {
			this.userMenuIsCollapsed = !this.userMenuIsCollapsed;
			this.featuresMenuIsCollapsed = true;
			this.settingsMenuIsCollapsed = true;
		} else if (menu === 'features') {
			this.userMenuIsCollapsed = true;
			this.featuresMenuIsCollapsed = !this.featuresMenuIsCollapsed;
			this.settingsMenuIsCollapsed = true;
		} else if (menu === 'settings') {
			this.userMenuIsCollapsed = true;
			this.featuresMenuIsCollapsed = true;
			this.settingsMenuIsCollapsed = !this.settingsMenuIsCollapsed;
		} else {
			this.userMenuIsCollapsed = true;
			this.featuresMenuIsCollapsed = true;
			this.settingsMenuIsCollapsed = true;
		}
	}

	/**
	 *logout the current user
	 */
	public logout() {
		const projectTimeId = this.projectTimeService.getCurrentProjectTimeSession();
		this.userTimeService.complete(projectTimeId);
		this.localStorageService.clearCurrentSessionTimeStart();
		this.authService.logout();
		this.router.navigate(['/' + Constants.ROUTE_LINKS.login]);
	}

	private setNavigationMenuConfig() {
		const isNewProjectButtonDisabled =
			!(this.rolePermissions.isTrainee() || this.rolePermissions.isUser() || this.rolePermissions.isTeamLeaderAndAbove()) || !this.canCreateNewProject;
		this.navConfig = new NavigationMenuConfig({
			logoURL: whiteLabelConfig.app.logoSideNav,
			navigationItems: [
				{
					route: '/create-project',
					iconURL: NavigationMenuIcons.filledFolder,
					toolTip: 'new project',
					isHidden: false,
					isDisabled: isNewProjectButtonDisabled,
					name: this.navigationMenuItems.newProject,
				},
				{
					route: '/',
					iconURL: NavigationMenuIcons.home,
					toolTip: 'dashboard',
					isHidden: false,
					name: this.navigationMenuItems.dashboard,
				},
				{
					route: '/projects',
					iconURL: NavigationMenuIcons.projects,
					toolTip: 'projects',
					isHidden: false,
					name: this.navigationMenuItems.projects,
				},
				{
					route: '/clients',
					iconURL: NavigationMenuIcons.clients,
					toolTip: 'clients',
					isHidden: false,
					name: this.navigationMenuItems.clients,
				},
				{
					route: '/reports',
					iconURL: NavigationMenuIcons.reporting,
					toolTip: 'reports',
					isHidden: !this.isContractsAndAbove,
					name: this.navigationMenuItems.reports,
				},
				{
					route: '/invoices',
					iconURL: NavigationMenuIcons.invoices,
					toolTip: 'invoices',
					isHidden: !this.isContractsAndAbove,
					name: this.navigationMenuItems.invoices,
				},
				{
					route: '/schedule',
					iconURL: NavigationMenuIcons.calendar,
					toolTip: 'Scheduling',
					name: this.navigationMenuItems.scheduling,
					isHidden: !this.features.has(Feature.ScheduleTool),
				},
				{
					route: '/libraries',
					iconURL: NavigationMenuIcons.database,
					toolTip: 'library',
					isHidden: !this.isContractsAndAbove,
					name: this.navigationMenuItems.library,
				},
			],
		});

		this.libraryRoutes = this.librariesService.getRoutes();
		this.settingsRoutes = this.settingsService.getRoutes();
	}

	@HostListener('window:keydown', ['$event'])
	public handleCommandPaletteKey(event: KeyboardEvent) {
		if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
			event.preventDefault();
			this.commandPalette.open();
		}
	}

	public openCommandPalette() {
		this.commandPalette.open();
	}

	private changeLanguage(lang: string): void {
		if (this.currentLanguage !== lang) {
			this.translocoService.setActiveLang(lang);
			this.translocoLocaleService.setLocale(lang === 'en-us' ? 'en-US' : 'en-AU');
			this.currentLanguage = lang;
		}
	}

	public get shouldShowContent(): boolean {
		return (
			this.isAuthenticated &&
			!this.isApproval &&
			!this.isResource &&
			!this.isMeasure &&
			!this.creatingProject &&
			this.isEntityActive &&
			!this.isEmployee &&
			!this.isCreateInvoice
		)
	}

	public get shouldUsePageContent(): boolean {
		return (
			this.isAuthenticated &&
			!this.isApproval &&
			!this.isResource &&
			!this.isMeasure &&
			!this.creatingProject &&
			!this.isOnGantt &&
			!this.isCreateInvoice
		)
	}

	private handleAuthState(): void {
		this.isAuthenticated = this.authService.isAuthenticated();
		if (this.isAuthenticated) {
			const user = this.authService.getUserDetail();
			// Log the user out if they have no entity assigned to them
			if (user && !user.entity) {
				this.logout();
			}

			// If authenticated, start recording new session time.
			this.localStorageService.setCurrentSessionTimeStart();
			this.userTimeService.startNew();
		}
	}

	private hasUserAcceptedTAndCs(): void {
		this.currentUser = this.localStorageService.getUserDetails();
		this.termsAndConditionsAccepted = this.currentUser.termsAndConditionsAccepted;
	}

	private configurePostHog(): void {
		if (environment.posthog) {
			// ensure feature flags are loaded by Posthog
			posthog.onFeatureFlags(() => {});
		}
	}

	private initUserDataStream(): void {
		this.userLoader = this.estimatorUserService.userDataStream.pipe(
			takeUntilDestroyed(this.destroyRef),
			tap((user: AuthUserModel) => {
				if (user?.token && user?.firstName && user?.role && user?.entity) {
					// Usually openIntercom would be in the authenticate function, but we currently don't run it on refresh
					// (instead we rely on isAuthenticated()) so I have put it here instead.
					this.intercomService.openIntercom(user);
					this.canCreateNewProject = user.entity.canCreateMoreProjects();
					this.communicationService.getCommunication();
					this.isAuthenticated = this.authService.isAuthenticated();
					this.isEntityActive = this.authService.isEntityActive();
					this.isContractsAndAbove = this.rolePermissions.isContractsAndAbove();
					this.isEmployee = this.rolePermissions.isEmployee();
					if (user.displayPictureKey && user.displayPictureKey !== 'undefined' && this.isAuthenticated) {
						this.exportService.getAwsKeyUrl({ awskey: user.displayPictureKey }).subscribe(signedUrl => {
							this.userPicture = signedUrl.awsKey;
						});
					}
					const targetLanguage = user.language ?? (user.entity.address.country === 'united states' ? 'en-us' : 'en-au');
					if (targetLanguage !== this.currentLanguage) {
						this.changeLanguage(targetLanguage);
					}

					// If user is authenticated and entity is active, load states in cache service and ensure user has accepted T&Cs
					if (this.isAuthenticated && this.isEntityActive) {
						this.cacheService.cacheAllStates();
						this.hasUserAcceptedTAndCs();
						this.fetchAccountingProvider();
					}
				} else {
					this.canCreateNewProject = false;
					this.isAuthenticated = false;
					this.isEntityActive = false;
					this.isContractsAndAbove = false;
					this.userPicture = Constants.PROFILE_DEFAULT_PICTURE;
				}

				this.setNavigationMenuConfig();
			})
		);
	}

	private handleNavigationAndUpdateUIFlags(): void {
		this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
			if (event instanceof NavigationEnd) {
				this.selectedRoute = event.url;

				this.isResource = this.selectedRoute.includes('/shifts');
				this.isMeasure = this.selectedRoute.includes('/measuring-tool');
				this.isOnSettings = this.selectedRoute.includes('/admin');
				this.isOnLibraries = this.selectedRoute.includes('/libraries');
				this.isOnGantt = this.selectedRoute.includes('/forecasting');
				this.creatingProject = this.selectedRoute.includes('/create-project');
				this.isCreateInvoice = this.selectedRoute.includes('/create-invoice')
				this.isApproval = this.selectedRoute.includes('/approval');
				this.isOnSalesPipeline = this.selectedRoute.includes('/pipeline');
				this.isOnExternalAccounting = this.selectedRoute.includes('/admin/external-accounting/');
				// Update Intercom on page change for logged in users
				if (this.authService.isAuthenticated()) {
					this.intercomService.updateIntercom(event);
					this.hasIntegrationWarnings();
				}
			}
		});
	}

	private startSaasAdminMessagePolling(): void {
		// Check for + refresh saas admin messages to display in the paint-communication component
		this.saasAdminMessagesSubscription.add(
			interval(TIMES_IN_MILLISECONDS.threeMinutes).pipe(
				takeUntilDestroyed(this.destroyRef))
				.subscribe(() => {
					if (this.isAuthenticated) {
						this.communicationService.getCommunication();
					}
				})
		);
	}

	public onScroll(): void {
		this.scrollService.onScroll();
	}

	private checkForAdminMessages() {
		this.communicationService.paintCommunication.pipe(
			takeUntilDestroyed(this.destroyRef),
			tap((messages) => {
				if (messages?.length > 0) {
					this.sassAdminMessages = true;
				}
			})
		).subscribe();
	}

	private hasIntegrationWarnings(): void {
		if (this.rolePermissions.isAdmin() && this.currentUser?.id && this.currentUser?.entity?.owner === this.currentUser.id) {
			this.accountingAuditService.hasLogWarning().pipe(
				takeUntilDestroyed(this.destroyRef)
			).subscribe({
				next: (hasWarning) => {
					this.hasIntegrationWarning = hasWarning;
				},
				error: (err) => {
					console.error('Error checking integration warnings:', err);
					this.hasIntegrationWarning = false;
				}
			});
		}
	}

	public onTermsAccepted(): void {
		this.termsAndConditionsAccepted = true;
		this.currentUser = this.authService.getUserDetail();
	}

	public onIntegrationWarningDismissed(): void {
		this.hasIntegrationWarning = false;

		// Call backend to mark warnings as dismissed (same as viewing logs)
		this.accountingAuditService.dismissLogWarnings().pipe(
			takeUntilDestroyed(this.destroyRef)
		).subscribe({
			error: (err) => {
				console.error('Error dismissing integration warning:', err);
			}
		});
	}

	private fetchAccountingProvider(): void {
		this.externalAccountingService.getConfig().pipe(
			takeUntilDestroyed(this.destroyRef)
		).subscribe({
			next: (result) => {
				this.accountingProvider = result.config?.provider;
			},
			error: (err) => {
				console.error('Error fetching accounting provider:', err);
			}
		});
	}
}
